import React, { useCallback, useState, useEffect } from "react";
import DashboardHeader from "../components/Other/DashboardHeader";
import { useOutletContext, useLocation } from "react-router-dom";
import PageHeaderTitle from "../components/pages/PageHeaderTitle";
import { useTranslation } from "react-i18next";
import PortfolioSection from "../components/Games/PortfolioSection";
import NewPortfolioSection from "../components/Games/NewPortfolioSection";
import NewTraderGuide from "../components/guide/NewTraderGuide";
import { useNewTraderGuide } from "../context/NewTraderGuideContext";
import usePullToRefresh from "../hooks/usePullToRefresh";
import PullToRefreshIndicator from "../components/Other/PullToRefreshIndicator";
import { fetchPortfolioMetrics, getGuideSetting } from "../api/user";
import PortfolioGuideSteps from "../components/guide/PortfolioGuideSteps";
import GuideLauncher from "../components/guide/GuideLauncher";

const BuySellPage = ({ forceLegacy = false, applyLogic = false }) => {
  const { t } = useTranslation();
  const [sidebarToggle] = useOutletContext();
  const location = useLocation();
  const { game_id } = location.state || {};
  const { guideActive, guideStep } = useNewTraderGuide();
  // const isPortfolioPage = location.pathname === "/portfolionew";
  const isPortfolioPage =
  /^\/portfolionew(\/\d+)?$/.test(location.pathname);
  const [, setRefreshTrigger] = useState(0);
  const [showGuide, setShowGuide] = useState(false);
  const [guideIcon, setGuideIcon] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const tabChangeRef = React.useRef(null);

  useEffect(() => {
    const fetchGuideStatus = async () => {
      try {
        const response = await getGuideSetting();
        if (response?.data?.success && response?.data?.status === 200) {
          setGuideIcon(response?.data?.show_icon);
        }
      } catch (error) {
        console.error("Error fetching guide status:", error);
      }
    };
    fetchGuideStatus();
  }, []);

  useEffect(() => {
    const sidebar = document.getElementById('sidebar');
    if (!sidebar) return;

    const checkSidebarOpen = () => {
      const isOpen = !sidebar.classList.contains('-translate-x-full');
      setIsSidebarOpen(isOpen);
    };

    checkSidebarOpen();

    const observer = new MutationObserver(checkSidebarOpen);
    observer.observe(sidebar, { attributes: true, attributeFilter: ['class'] });

    return () => observer.disconnect();
  }, []);

  const refreshFunction = useCallback(async () => {
    try {
      await fetchPortfolioMetrics({ game_id: game_id || null });
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error("Error refreshing portfolio:", error);
    }
  }, [game_id]);

  const { containerProps, isRefreshing, pullDistance } = usePullToRefresh({
    onRefresh: refreshFunction,
    disabled: false,
  });

  const handleTabChange = (tabKey) => {
    if (tabChangeRef.current) {
      tabChangeRef.current(tabKey);
    }
  };


  return (
    <>
      <div {...containerProps}>
        <DashboardHeader toggle={sidebarToggle} />
        {/* Desktop: hide the "Portfolio" title + grey strip (the sidebar already
            marks the active page). Mobile spacing/header is unaffected. */}
        <PageHeaderTitle title={t("Portfolio")} showDesktopTitle={false} />
        {/* DashboardHeader above is `position: fixed`; PageHeaderTitle is the spacer
            that clears it. The indicator must render AFTER that spacer, not before —
            otherwise its growing box sits in the dead zone hidden behind the fixed
            header instead of the visible area below it. */}
        <PullToRefreshIndicator pullDistance={pullDistance} isRefreshing={isRefreshing} />
        {guideActive && guideStep === 4 && <NewTraderGuide step={4} />}
        {guideActive && guideStep === 5 && <NewTraderGuide step={5} />}

        {guideIcon && !showGuide && !isSidebarOpen && <GuideLauncher onOpen={() => setShowGuide(true)} />}

        {showGuide && (
          <PortfolioGuideSteps setShowGuide={setShowGuide} onTabChange={handleTabChange} />
        )}

        {isPortfolioPage ? (
            <NewPortfolioSection
              ref={tabChangeRef}
              gameId={game_id}
              slug={null}
              user_id={null}
              isSelfProfile={true}
              ignoreDesign={true}
              forceLegacy={forceLegacy}
              applyLogic={applyLogic}
              disableInternalGuide={true}
            />
          ) : (
            <NewPortfolioSection
              ref={tabChangeRef}
              gameId={game_id}
              slug={null}
              user_id={null}
              isSelfProfile={true}
              ignoreDesign={true}
              forceLegacy={forceLegacy}
              applyLogic={applyLogic}
              disableInternalGuide={true}
            />
          )}
      </div>
    </>
  );
};

export default BuySellPage;
